import type React from 'react';
import { useEffect, useMemo } from 'react';
import type { DefaultOptionType } from 'antd/lib/select';
import { FormattedMessage, useIntl } from '@/components/formatted_message/FormattedMessage';
import { useEstablishments } from '@/services/hooks/establishments/useEstablishments';
import { DEFAULT_LOCATION_ID, type SerializedEstablishment, ViewEstablishments } from '@enmon/commons/lib/enmon';
import { Establishments as messages } from '@/components/filter/messages';
import type { GetEstablishmentsArg } from '@/services/api/establishments/types';
import { removeAccents } from '@/utils/string/removeAccents';
import { TreeSelect, type TreeSelectProps } from './TreeSelect';
import { parseMaybeRegExp } from './utils/parseMaybeRegExp';
import { transformAndSortEstablishmentsData } from './utils/transformAndSortEstablishmentsData';

const { SHOW_PARENT } = TreeSelect;

const filterTreeNode = (
  inputValue: string,
  // DefaultOptionType for TreeSelect is not exported directly from antd
  treeNode: Omit<DefaultOptionType, 'label'> & { label?: React.ReactNode },
): boolean => {
  const q = parseMaybeRegExp(inputValue, 'i');
  const { label } = treeNode;

  // Checks if input has a match with label using RegExp
  const isFindableByRegexp = typeof label === 'string' ? q.test(label) : false;

  // Checks if input has a match with label using regular .includes
  const isInputMatched =
    typeof label === 'string'
      ? removeAccents(label)?.toLowerCase().includes(removeAccents(inputValue).toLowerCase().trim())
      : false;

  return isFindableByRegexp || isInputMatched;
};

export interface EstablishmentsTreeSelectProps
  extends Pick<TreeSelectProps, 'disabled' | 'treeData' | 'allowClear' | 'placeholder'> {
  value?: SerializedEstablishment['_id'] | SerializedEstablishment['_id'][];
  filter?: GetEstablishmentsArg;
  onChange?: (value: SerializedEstablishment['_id'][] | undefined) => void;
  onOpen?: () => void;
}

export const EstablishmentsTreeSelect: React.VFC<EstablishmentsTreeSelectProps> = ({
  value,
  filter,
  allowClear,
  onChange,
  onOpen,
  ...restProps
}) => {
  const intl = useIntl();
  const { data: establishmentData, isLoading } = useEstablishments(filter ?? {}, ViewEstablishments.Dropdown);

  // reset value if establishments are not contained in the loaded data
  useEffect(() => {
    if (!establishmentData) return;

    const { result } = establishmentData;
    const uniqueIds = result.flatMap(({ _id: locationId, buildings }) => [
      locationId,
      ...buildings.map(({ _id: buildingId }) => buildingId),
    ]);

    // If some establishments are set and the loaded data does not contain some of them, reset this field.
    if (Array.isArray(value) && value.some(establishmentId => !uniqueIds.includes(establishmentId)))
      onChange?.(undefined);
    else if (typeof value === 'string' && !uniqueIds.includes(value)) onChange?.(undefined);
  }, [establishmentData, value, onChange]);

  const treeData = useMemo(() => {
    if (!establishmentData) return undefined;

    const { result } = establishmentData;

    if (result.length === 1 && result[0]?._id === DEFAULT_LOCATION_ID) {
      return transformAndSortEstablishmentsData(result[0]?.buildings, intl);
    }
    return transformAndSortEstablishmentsData(result, intl);
  }, [establishmentData, intl]);

  return (
    <TreeSelect
      treeCheckable
      allowClear={allowClear}
      data-cy="filter-field-select-establishments"
      filterTreeNode={filterTreeNode}
      showSearch
      id="tree-select-establishments"
      listHeight={400}
      loading={isLoading}
      maxTagCount="responsive"
      placeholder={<FormattedMessage {...messages.placeholder} />}
      popupMatchSelectWidth={false}
      showCheckedStrategy={SHOW_PARENT}
      treeData={treeData}
      treeLine={{ showLeafIcon: false }}
      treeNodeFilterProp="label"
      value={typeof value === 'string' ? [value] : value}
      onChange={onChange}
      multiple={false}
      onDropdownVisibleChange={open => {
        if (open) onOpen?.();
      }}
      {...restProps}
    />
  );
};
